<script setup lang="ts">
import { faArrowRight, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { ReserveType } from '@utils/itinerary';
import { TripType } from '@utils/trip';

const { disableChangeSearch } = defineProps<{
    hideMap?: boolean;
    showHotelsOnMap?: boolean;
    showingSuggestions?: boolean;
    changeSearchFixed?: boolean;
    hideDateInTitle?: boolean;
    disableChangeSearch?: boolean;
}>();

const emit = defineEmits<{
    showSearch: [];
    toggleMaps: [];
}>();

const MapBtn = defineAsyncComponent(() => import('./MapBtn.vue'));

const itineraryStore = useItineraryStore();
const currentModule = useCurrentItineraryModule();
const { mainTitle, dateTitle } = moduleToRefs(currentModule.value);
const { isSelectingReturn, expectedPackagesToBeSelected } =
    storeToRefs(itineraryStore);
const searchFormStore = useSearchFormStore();
const { reserveType, haveSuggestions } = storeToRefs(searchFormStore);
const flightForm = useFlightForm();
const { tripType, isDomestic } = moduleToRefs(flightForm);

const columnTitleText = computed(
    () =>
        reserveType.value === ReserveType.Flight &&
        !isDomestic.value &&
        tripType.value === TripType.RoundTrip
);

const router = useRouter();

const isHotelReserve = computed(() => reserveType.value === ReserveType.Hotel);

const showTicketHelper = computed(() => {
    if (expectedPackagesToBeSelected.value === 2) {
        return !(reserveType.value === ReserveType.Flight && !isDomestic.value);
    }
    return false;
});
const onClick = () => {
    if (!disableChangeSearch) {
        emit('showSearch');
    }
};
</script>
<template>
    <div
        class="page-title-wrapper"
        :class="{
            'show-map': showHotelsOnMap,
            hotel: isHotelReserve,
        }"
    >
        <div
            class="page-title-container"
            :class="{
                'change-search-fixed': changeSearchFixed,
                'suggestion-show':
                    !showHotelsOnMap && showingSuggestions && changeSearchFixed,
                hotel: isHotelReserve,
            }"
            @click="onClick"
        >
            <mr-button
                v-if="!haveSuggestions"
                key="back-button-mobile"
                :icon-button="{
                    icon: faArrowRight,
                    color: 'white',
                }"
                size="medium"
                variant="transparent"
                class="mobile-only back-home-button"
                @click.stop="router.push('/')"
            />
            <div
                class="title-texts"
                :class="{
                    'column-title-text': columnTitleText,
                }"
            >
                <mr-typography
                    v-if="showTicketHelper"
                    class="prefix mobile-only"
                    color="white"
                    weight="700"
                >
                    {{ isSelectingReturn ? 'برگشت' : 'رفت' }}&nbsp;:&nbsp;
                </mr-typography>
                <mr-typography
                    type="h1"
                    class="main-title"
                    white-space="normal"
                    :size="{
                        mobile: 'medium',
                        desktop: 'large',
                    }"
                    :weight="{
                        mobile: '500',
                        desktop: '700',
                    }"
                >
                    {{ mainTitle }}
                </mr-typography>
                <ClientOnly>
                    <mr-typography
                        v-if="!hideDateInTitle"
                        color="white"
                        class="suffix mobile-only"
                        weight="500"
                        white-space="nowrap"
                    >
                        &nbsp;{{ dateTitle }}
                    </mr-typography>
                    <template #fallback />
                </ClientOnly>
            </div>
            <mr-icon
                v-if="!disableChangeSearch"
                class="search-icon mobile-only"
                :icon="faSearch"
                color="white"
            />
        </div>
    </div>
    <ClientOnly>
        <MapBtn v-if="isHotelReserve && !hideMap" @click="emit('toggleMaps')" />
        <template #fallback />
    </ClientOnly>
</template>
<style scoped lang="scss">
.page-title-wrapper {
    @include mobile-only {
        position: relative;
        height: 64px;
        &.hotel {
            height: 48px;
        }
        &.show-map {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 10000000000;
        }
    }
    .page-title-container {
        display: flex;
        align-items: center;
        height: 72px;
        @include shadow(1);
        background: white;
        border-radius: 12px;
        padding: 16px;
        @include mobile-only {
            height: 64px;
            @include shadow(0);
            border-radius: 0;
            width: 100%;
            background: usePalette();
            margin: 0;
            z-index: 1001;
            position: absolute;
            top: 0;
            &.change-search-fixed {
                position: fixed;
            }
            &.suggestion-show {
                transform: translateY(48px);
            }
            .back-home-button {
                flex: 0 0 40px;
            }

            .title-texts {
                flex: 1 1 auto;
                display: flex;
                align-items: center;
                overflow: hidden;
                &.column-title-text {
                    flex-direction: column;
                    align-items: flex-start;
                }
                .prefix {
                    flex: 0 0 auto;
                }
                .main-title {
                    flex: 0 0 auto;
                    color: white !important;
                }
                .suffix {
                    flex: 0 1 auto;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
            .search-icon {
                flex: 0 0 18px;
                &:before {
                    color: white;
                }
            }
        }
        h1 {
            color: usePalette($p-blue, $p-600) !important;
            margin: 0;
        }
        &.hotel {
            @include shadow(0);
            height: 48px;
            padding: 8px 12px;
            background-color: transparent;
            @include mobile-only {
                background-color: usePalette();
            }
        }
    }
}
</style>
